import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  TextInput,
  Pressable,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import { StrategicGoalList } from '../types';
import { COLORS, FONTS } from '../constants/config';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';
import { goalsApi } from '../services/apiService';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
// ============================================================
// SVG Icon Components
// ============================================================

const CheckCircleIcon = ({ size = 20, color = '#16A34A' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
    <Path d="M8 12L11 15L16 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const XCircleIcon = ({ size = 20, color = tokens.colors.raw.red["600"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
    <Path d="M15 9L9 15M9 9L15 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ClockIcon = ({ size = 16, color = tokens.colors.raw.yellow["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
    <Path d="M12 6V12L16 14" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const CloseIcon = ({ size = 24, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// ============================================================
// Props Interface
// ============================================================

interface PendingGoalApprovalSectionProps {
  pendingGoals: StrategicGoalList[];
  onRefresh: () => void;
}

// ============================================================
// Main Component
// ============================================================

export default function PendingGoalApprovalSection({
  pendingGoals,
  onRefresh,
}: PendingGoalApprovalSectionProps) {
  const { t } = useLanguage();
  const { colors, isDark } = useTheme();
  const { showNotification } = useNotification();

  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    sectionBg: isDark ? tokens.colors.raw.gray["800"] : '#FFF9E6',
    sectionBorder: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.yellow["500"],
  };

  const handleApprove = async (goalId: number) => {
    try {
      setLoading(true);
      await goalsApi.approve(goalId);
      showNotification(t('approval.approvalSuccess'), 'success');
      onRefresh();
    } catch (error: any) {
      console.error('Error approving goal:', error);
      showNotification(
        error.response?.data?.error || t('approval.approvalError'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClick = (goalId: number) => {
    setSelectedGoalId(goalId);
    setRejectionReason('');
    setRejectModalVisible(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedGoalId) return;

    if (!rejectionReason.trim()) {
      showNotification(t('approval.pleaseEnterReason'), 'error');
      return;
    }

    try {
      setLoading(true);
      await goalsApi.reject(selectedGoalId, rejectionReason);
      showNotification(t('approval.rejectionSuccess'), 'success');
      setRejectModalVisible(false);
      setSelectedGoalId(null);
      setRejectionReason('');
      onRefresh();
    } catch (error: any) {
      console.error('Error rejecting goal:', error);
      showNotification(
        error.response?.data?.error || t('approval.rejectionError'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  if (pendingGoals.length === 0) {
    return null;
  }

  return (
    <>
      {/* Pending Requests Section */}
      <View
        style={[
          styles.pendingSection,
          {
            backgroundColor: themeColors.sectionBg,
            borderStartColor: themeColors.sectionBorder,
          },
        ]}
      >
        {/* Header */}
        <View style={styles.pendingSectionHeader}>
          <View style={styles.pendingHeaderContent}>
            <ClockIcon size={20} color={tokens.colors.raw.yellow["500"]} />
            <Text style={[styles.pendingSectionTitle, { color: themeColors.textPrimary }]}>
              {t('approval.pendingRequests')} ({pendingGoals.length})
            </Text>
          </View>
        </View>

        {/* Pending Goals List */}
        <View style={styles.pendingGoalsList}>
          {pendingGoals.map((goal) => (
            <View
              key={goal.id}
              style={[
                styles.pendingGoalCard,
                {
                  backgroundColor: themeColors.card,
                  borderColor: themeColors.cardBorder,
                },
              ]}
            >
              {/* Goal Info */}
              <View style={styles.pendingGoalInfo}>
                <Text style={[styles.pendingGoalTitle, { color: themeColors.textPrimary }]} numberOfLines={2}>
                  {goal.title}
                </Text>
                {goal.description && (
                  <Text style={[styles.pendingGoalDescription, { color: themeColors.textSecondary }]} numberOfLines={2}>
                    {goal.description}
                  </Text>
                )}
                {goal.submitted_by_details && goal.submitted_at && (
                  <Text style={[styles.pendingGoalMeta, { color: themeColors.textMuted }]}>
                    {t('approval.submittedBy')}: {goal.submitted_by_details.full_name} •{' '}
                    {new Date(goal.submitted_at).toLocaleDateString('ar-AE')}
                  </Text>
                )}
              </View>

              {/* Action Buttons */}
              <View style={styles.pendingGoalActions}>
                <Button
                  variant="primary"
                  onPress={() => handleApprove(goal.id)}
                  loading={loading}
                  disabled={loading}
                  leadingIcon={<CheckCircleIcon size={18} color={tokens.colors.raw.white} />}
                >
                  {t('approval.approve')}
                </Button>

                <Button
                  variant="destructive"
                  onPress={() => handleRejectClick(goal.id)}
                  disabled={loading}
                  leadingIcon={<XCircleIcon size={18} color={tokens.colors.raw.white} />}
                >
                  {t('approval.reject')}
                </Button>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Rejection Reason Modal */}
      <Modal
        visible={rejectModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setRejectModalVisible(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setRejectModalVisible(false)}
        >
          <Pressable
            style={[styles.modalContent, { backgroundColor: themeColors.card }]}
            onPress={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: themeColors.textPrimary }]}>
                {t('approval.rejectionReason')}
              </Text>
              <TouchableOpacity
                onPress={() => setRejectModalVisible(false)}
                style={styles.modalCloseButton}
              >
                <CloseIcon size={24} color={themeColors.textMuted} />
              </TouchableOpacity>
            </View>

            {/* Rejection Reason Input */}
            <TextInput
              style={[
                styles.reasonInput,
                {
                  backgroundColor: isDark ? tokens.colors.raw.gray["800"] : tokens.colors.raw.gray["50"],
                  color: themeColors.textPrimary,
                  borderColor: themeColors.cardBorder,
                },
              ]}
              multiline
              numberOfLines={4}
              value={rejectionReason}
              onChangeText={setRejectionReason}
              placeholder={t('approval.enterRejectionReason')}
              placeholderTextColor={themeColors.textMuted}
              textAlign="right"
            />

            {/* Modal Actions */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalCancelButton, { borderColor: themeColors.cardBorder }]}
                onPress={() => setRejectModalVisible(false)}
              >
                <Text style={[styles.modalCancelButtonText, { color: themeColors.textPrimary }]}>
                  {t('common.cancel')}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.modalConfirmButton, loading && styles.buttonDisabled]}
                onPress={handleRejectConfirm}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size="small" color={tokens.colors.raw.white} />
                ) : (
                  <Text style={styles.modalConfirmButtonText}>{t('approval.reject')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  pendingSection: {
    backgroundColor: '#FFF9E6',
    borderStartWidth: 4,
    borderStartColor: tokens.colors.raw.yellow["500"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["5"],
  },
  pendingSectionHeader: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  pendingHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  pendingSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.semiBold,
    color: tokens.colors.semantic.general.foreground,
  },
  pendingGoalsList: {
    gap: tokens.spacing.absolute["3"],
  },
  pendingGoalCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["3"],
  },
  pendingGoalInfo: {
    gap: tokens.spacing.absolute["2"],
  },
  pendingGoalTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.semiBold,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  pendingGoalDescription: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  pendingGoalMeta: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
    textAlign: getTextAlign(isRTL),
  },
  pendingGoalActions: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["2"],
  },
  buttonDisabled: {
    opacity: 0.6,
  },

  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.absolute["5"],
  },
  modalContent: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: '100%',
    maxWidth: 500,
    gap: tokens.spacing.absolute["5"],
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.semiBold,
    color: tokens.colors.semantic.general.foreground,
  },
  modalCloseButton: {
    padding: tokens.spacing.absolute["1"],
  },
  reasonInput: {
    backgroundColor: tokens.colors.raw.gray["50"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    minHeight: 100,
    textAlignVertical: 'top',
  },
  modalActions: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["3"],
  },
  modalCancelButton: {
    flex: 1,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-6"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  modalConfirmButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.red["600"],
    borderRadius: tokens.radii.absolute["radius-6"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
  },
  modalConfirmButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
});

const styles = getStyles(true);
